import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  AlertTriangle, ArrowLeft, Building2, Check, ClipboardCopy,
  KeyRound, Pencil, Plus, Trash2, UserPlus, X,
} from 'lucide-react';
import {
  useCreateClientUser,
  useDisableClientUser,
  useOrganisation,
  useOrganisationUsers,
  useRenameOrganisation,
  useResetClientUserPassword,
  type ClientUser,
  type CreateClientUserResult,
} from '../../lib/api/queries';

/**
 * `/organisations/:id` — detail page for a single client organisation.
 * Layout:
 *
 *   ← Back to list
 *   Big org name with inline rename
 *   Client users section: list + "Add client user" button
 *
 * Creating a client user opens a modal; on success the modal transitions
 * to a one-shot "temporary password" screen that the admin copies once
 * and shares out-of-band with the client.
 */
export function OrganisationDetailPage() {
  const { id } = useParams<{ id: string }>();
  const org = useOrganisation(id);
  const users = useOrganisationUsers(id);

  if (org.isLoading) return <p className="text-sm text-gray-500">Loading…</p>;
  if (org.error || !org.data) {
    return (
      <div className="space-y-4">
        <BackLink />
        <p className="text-sm text-danger-500">
          Couldn't load this organisation. It may have been renamed or you
          don't have access.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <BackLink />
      <Header org={org.data} />
      <UsersSection
        organisationId={org.data.id}
        users={users.data ?? []}
        isLoading={users.isLoading}
      />
    </div>
  );
}

// -----------------------------------------------------------------------

function BackLink() {
  return (
    <Link to="/organisations"
          className="inline-flex items-center gap-1.5 text-sm text-brand-600 hover:text-brand-700">
      <ArrowLeft className="w-4 h-4" aria-hidden />
      Back to organisations
    </Link>
  );
}

function Header({ org }: { org: import('../../lib/api/queries').Organisation }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(org.name);
  const [err, setErr] = useState<string | null>(null);
  const rename = useRenameOrganisation(org.id);

  const commit = async () => {
    setErr(null);
    const trimmed = name.trim();
    if (trimmed.length === 0) { setErr('Name is required.'); return; }
    if (trimmed === org.name) { setEditing(false); return; }
    try {
      await rename.mutateAsync({ name: trimmed });
      setEditing(false);
    } catch (e) {
      const status = (e as { status?: number })?.status;
      if (status === 409) {
        setErr('Another organisation in your tenant already uses that name.');
      } else {
        setErr('Failed to rename. Try again.');
      }
    }
  };

  return (
    <header className="border-b border-gray-200 pb-6">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3 min-w-0">
          <div className="w-12 h-12 rounded-md bg-brand-50 text-brand-600
                          flex items-center justify-center shrink-0">
            <Building2 className="w-6 h-6" aria-hidden />
          </div>
          <div className="min-w-0">
            {editing ? (
              <div className="flex items-center gap-2">
                <input type="text"
                       autoFocus
                       value={name}
                       onChange={e => setName(e.target.value)}
                       onKeyDown={e => {
                         if (e.key === 'Enter') commit();
                         if (e.key === 'Escape') { setEditing(false); setName(org.name); }
                       }}
                       className="text-2xl font-semibold border-b-2 border-brand-500
                                  focus:outline-none min-w-0 w-full max-w-lg" />
                <button type="button"
                        onClick={commit}
                        disabled={rename.isPending}
                        className="p-1.5 rounded-md text-brand-600 hover:bg-brand-50
                                   disabled:opacity-50">
                  <Check className="w-5 h-5" aria-hidden />
                </button>
                <button type="button"
                        onClick={() => { setEditing(false); setName(org.name); setErr(null); }}
                        className="p-1.5 rounded-md text-gray-500 hover:bg-gray-100">
                  <X className="w-5 h-5" aria-hidden />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-semibold text-gray-900 truncate">
                  {org.name}
                </h1>
                <button type="button"
                        onClick={() => setEditing(true)}
                        className="p-1.5 rounded-md text-gray-400 hover:bg-gray-100
                                   hover:text-gray-600"
                        title="Rename">
                  <Pencil className="w-4 h-4" aria-hidden />
                </button>
              </div>
            )}
            <p className="text-sm text-gray-500 mt-1">
              {org.userCount} client user{org.userCount === 1 ? '' : 's'}
              {' · '}{org.auditCount} audit{org.auditCount === 1 ? '' : 's'}
              {' · '}added {new Date(org.createdAtUtc).toLocaleDateString()}
            </p>
          </div>
        </div>
      </div>
      {err && <p className="text-sm text-danger-500 mt-3">{err}</p>}
    </header>
  );
}

// -----------------------------------------------------------------------

function UsersSection({
  organisationId,
  users,
  isLoading,
}: {
  organisationId: string;
  users: ClientUser[];
  isLoading: boolean;
}) {
  const [creating, setCreating] = useState(false);
  const [resetResult, setResetResult] = useState<CreateClientUserResult | null>(null);
  const disable = useDisableClientUser(organisationId);
  const reset   = useResetClientUserPassword(organisationId);

  const onDisable = async (u: ClientUser) => {
    const ok = window.confirm(
      `Disable ${u.displayName}? They will no longer be able to sign in ` +
      `or view your reports. This does not delete their historical data.`);
    if (!ok) return;
    try {
      await disable.mutateAsync(u.id);
    } catch {
      window.alert('Failed to disable the user. Try again.');
    }
  };

  const onReset = async (u: ClientUser) => {
    const ok = window.confirm(
      `Reset ${u.displayName}'s password? Their current password will stop ` +
      `working immediately and a fresh temporary password will be issued. ` +
      `You'll need to share it with them out-of-band.`);
    if (!ok) return;
    try {
      const result = await reset.mutateAsync(u.id);
      setResetResult(result);
    } catch {
      window.alert('Failed to reset the password. Try again.');
    }
  };

  return (
    <section>
      <div className="flex items-center justify-between gap-4 mb-3">
        <h2 className="text-lg font-semibold text-gray-900">Client users</h2>
        <button type="button"
                onClick={() => setCreating(true)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium
                           text-white bg-brand-600 hover:bg-brand-700 rounded-md
                           transition-colors">
          <UserPlus className="w-4 h-4" aria-hidden /> Add client user
        </button>
      </div>

      {isLoading && <p className="text-sm text-gray-500">Loading users…</p>}
      {!isLoading && users.length === 0 && (
        <div className="rounded-md border border-gray-200 bg-white p-6 text-center">
          <p className="text-sm text-gray-600">No client users yet.</p>
          <p className="text-xs text-gray-500 mt-1">
            Add a user so this organisation can see the reports you publish
            to them.
          </p>
        </div>
      )}
      {users.length > 0 && (
        <ul className="bg-white border border-gray-200 rounded-lg divide-y divide-gray-100">
          {users.map(u => (
            <li key={u.id} className="p-3 flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center
                              text-xs font-semibold text-gray-600 shrink-0">
                {initials(u)}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {u.displayName}
                </p>
                <p className="text-xs text-gray-500 truncate">{u.email}</p>
              </div>
              {u.isDisabled ? (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px]
                                 font-semibold uppercase tracking-wide text-warning-700
                                 bg-warning-50 rounded">
                  Disabled
                </span>
              ) : (
                <>
                  <button type="button"
                          onClick={() => onReset(u)}
                          disabled={reset.isPending}
                          title="Reset this user's password"
                          className="p-1.5 rounded-md text-gray-400 hover:bg-brand-50
                                     hover:text-brand-600 disabled:opacity-50">
                    <KeyRound className="w-4 h-4" aria-hidden />
                  </button>
                  <button type="button"
                          onClick={() => onDisable(u)}
                          disabled={disable.isPending}
                          title="Disable this user"
                          className="p-1.5 rounded-md text-gray-400 hover:bg-danger-50
                                     hover:text-danger-500 disabled:opacity-50">
                    <Trash2 className="w-4 h-4" aria-hidden />
                  </button>
                </>
              )}
            </li>
          ))}
        </ul>
      )}

      {creating && (
        <CreateClientUserModal
          organisationId={organisationId}
          onClose={() => setCreating(false)}
        />
      )}

      {resetResult && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center px-4"
             onClick={() => setResetResult(null)}>
          <div onClick={e => e.stopPropagation()}
               className="bg-white w-full max-w-md rounded-lg shadow-xl overflow-hidden">
            <TempPasswordPanel
              result={resetResult}
              mode="reset"
              onDone={() => setResetResult(null)}
            />
          </div>
        </div>
      )}
    </section>
  );
}

// -----------------------------------------------------------------------

function CreateClientUserModal({
  organisationId,
  onClose,
}: {
  organisationId: string;
  onClose: () => void;
}) {
  const [email, setEmail]         = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName]   = useState('');
  const [err, setErr]             = useState<string | null>(null);
  const [created, setCreated]     = useState<CreateClientUserResult | null>(null);
  const create = useCreateClientUser(organisationId);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    if (email.trim().length === 0) {
      setErr('Email is required.');
      return;
    }
    try {
      const result = await create.mutateAsync({
        email:     email.trim(),
        firstName: firstName.trim() || null,
        lastName:  lastName.trim()  || null,
      });
      setCreated(result);
    } catch (e) {
      const status = (e as { status?: number })?.status;
      const body   = (e as { body?: unknown })?.body;
      const msg    = typeof body === 'string' ? body : null;
      if (status === 409) {
        setErr(msg ?? 'That email is already in use.');
      } else if (status === 400) {
        setErr(msg ?? 'The details didn\'t pass validation.');
      } else {
        setErr('Failed to create user. Try again.');
      }
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center px-4"
         onClick={onClose}>
      <div onClick={e => e.stopPropagation()}
           className="bg-white w-full max-w-md rounded-lg shadow-xl overflow-hidden">
        {created ? (
          <TempPasswordPanel result={created} mode="created" onDone={onClose} />
        ) : (
          <form onSubmit={submit}>
            <header className="px-5 py-3 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
              <h2 className="text-base font-semibold text-gray-900">Add client user</h2>
              <button type="button" onClick={onClose}
                      className="text-gray-500 hover:bg-gray-100 rounded-md w-8 h-8
                                 inline-flex items-center justify-center">
                <X className="w-5 h-5" aria-hidden />
              </button>
            </header>
            <div className="px-5 py-4 space-y-4">
              <label className="block">
                <span className="text-sm font-medium text-gray-700">Email</span>
                <input type="email"
                       autoFocus
                       value={email}
                       onChange={e => setEmail(e.target.value)}
                       placeholder="jane@voluptatem.example"
                       className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm
                                  focus:outline-none focus:ring-2 focus:ring-brand-500" />
              </label>
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="text-sm font-medium text-gray-700">First name</span>
                  <input type="text"
                         value={firstName}
                         onChange={e => setFirstName(e.target.value)}
                         className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm
                                    focus:outline-none focus:ring-2 focus:ring-brand-500" />
                </label>
                <label className="block">
                  <span className="text-sm font-medium text-gray-700">Last name</span>
                  <input type="text"
                         value={lastName}
                         onChange={e => setLastName(e.target.value)}
                         className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm
                                    focus:outline-none focus:ring-2 focus:ring-brand-500" />
                </label>
              </div>
              <p className="text-xs text-gray-500">
                We'll generate a temporary password and show it to you once
                on the next screen. Share it with the client out-of-band —
                we can't retrieve it later.
              </p>
              {err && <p className="text-sm text-danger-500">{err}</p>}
            </div>
            <footer className="px-5 py-3 border-t border-gray-200 bg-gray-50 flex items-center justify-end gap-2">
              <button type="button"
                      onClick={onClose}
                      disabled={create.isPending}
                      className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white
                                 border border-gray-300 rounded-md hover:bg-gray-50
                                 transition-colors disabled:opacity-50">
                Cancel
              </button>
              <button type="submit"
                      disabled={create.isPending}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium
                                 text-white bg-brand-600 hover:bg-brand-700 rounded-md
                                 transition-colors disabled:opacity-50">
                <Plus className="w-4 h-4" aria-hidden />
                {create.isPending ? 'Creating…' : 'Create client user'}
              </button>
            </footer>
          </form>
        )}
      </div>
    </div>
  );
}

function TempPasswordPanel({
  result,
  mode,
  onDone,
}: {
  result: CreateClientUserResult;
  /** 'created' after Add client user, 'reset' after Reset password. */
  mode: 'created' | 'reset';
  onDone: () => void;
}) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(result.temporaryPassword);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // Old browser without Clipboard API — user can select manually.
    }
  };

  const heading = mode === 'created'
    ? 'Client user created'
    : 'Temporary password issued';

  return (
    <div>
      <header className="px-5 py-3 border-b border-gray-200 bg-brand-50">
        <h2 className="text-base font-semibold text-brand-900 flex items-center gap-2">
          <Check className="w-5 h-5" aria-hidden /> {heading}
        </h2>
      </header>
      <div className="px-5 py-4 space-y-4">
        <div>
          <p className="text-sm text-gray-700">
            <strong className="font-semibold text-gray-900">{result.user.displayName}</strong>
            {' '}<span className="text-gray-500">({result.user.email})</span>
          </p>
        </div>

        <div className="rounded-md border border-warning-200 bg-warning-50 p-3 flex gap-3">
          <AlertTriangle className="w-5 h-5 text-warning-500 shrink-0 mt-0.5" aria-hidden />
          <div className="text-xs text-warning-800">
            <p className="font-medium">One-time temporary password</p>
            <p className="mt-1">
              This is the only time we'll show it. Share it with the client
              through a secure channel (Signal, an in-person handoff, or an
              encrypted email). They'll be prompted to change it after their
              first sign-in.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <code className="flex-1 font-mono text-sm bg-gray-100 border border-gray-200
                            rounded-md px-3 py-2 select-all">
            {result.temporaryPassword}
          </code>
          <button type="button"
                  onClick={copy}
                  className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium
                             text-gray-700 bg-white border border-gray-300 rounded-md
                             hover:bg-gray-50 transition-colors">
            {copied ? <Check className="w-4 h-4" aria-hidden /> : <ClipboardCopy className="w-4 h-4" aria-hidden />}
            {copied ? 'Copied' : 'Copy'}
          </button>
        </div>
      </div>
      <footer className="px-5 py-3 border-t border-gray-200 bg-gray-50 flex justify-end">
        <button type="button"
                onClick={onDone}
                className="px-3 py-1.5 text-sm font-medium text-white bg-brand-600
                           hover:bg-brand-700 rounded-md transition-colors">
          Done
        </button>
      </footer>
    </div>
  );
}

function initials(u: ClientUser): string {
  const first = (u.firstName ?? '').trim();
  const last  = (u.lastName  ?? '').trim();
  if (first || last) return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
  return (u.email.charAt(0) || '?').toUpperCase();
}
